import posthog from 'posthog-js'

import config from 'lib/config'

import {
  PROFILE_FLAG_STORAGE_KEY,
  PROFILE_FLAGS,
  ProfileFlag,
} from './constants'

// Mirrors the de-duplication cache when localStorage is unavailable (private
// mode). Worst case a flag is re-sent — PostHog treats it as idempotent.
let memorySentFlags: ProfileFlag[] | null = null

const readSentFlags = (): ProfileFlag[] => {
  try {
    const raw = window?.localStorage?.getItem(PROFILE_FLAG_STORAGE_KEY)
    if (!raw) {
      return memorySentFlags ?? []
    }
    return JSON.parse(raw)
  } catch {
    return memorySentFlags ?? []
  }
}

const writeSentFlags = (flags: ProfileFlag[]): void => {
  memorySentFlags = flags
  try {
    window?.localStorage?.setItem(
      PROFILE_FLAG_STORAGE_KEY,
      JSON.stringify(flags)
    )
  } catch {
    // Private mode / quota. memorySentFlags already holds the update.
  }
}

// With person_profiles: 'identified_only', person properties sent while the
// visitor is still anonymous are dropped server-side. Skipping without marking
// the flag as sent means the next call retries it after login.
const canWriteProfile = (): boolean =>
  Boolean(config.POSTHOG_API_KEY) && Boolean(posthog?._isIdentified?.())

// Raises a lifecycle flag to true, at most once per agent per browser.
export const setProfileFlag = (flag: ProfileFlag): void => {
  const sent = readSentFlags()
  if (sent.includes(flag)) {
    return
  }
  if (!canWriteProfile()) {
    return
  }
  posthog?.setPersonProperties?.({ [flag]: true })
  writeSentFlags([...sent, flag])
}

// Seeds every flag as false the first time we see this person, so the property
// always exists and "never visited the demo store" is a filterable value rather
// than an absence. $set_once means a flag already raised is left untouched.
export const initProfileFlags = (): void => {
  if (!canWriteProfile()) {
    return
  }
  const seeded = PROFILE_FLAGS.reduce(
    (acc, flag) => ({ ...acc, [flag]: false }),
    {}
  )
  posthog?.setPersonProperties?.({}, seeded)
}

// Called on logout: the next agent signing in on this browser must send their
// own flags rather than inheriting the previous agent's cache.
export const clearProfileFlags = (): void => {
  memorySentFlags = null
  try {
    window?.localStorage?.removeItem(PROFILE_FLAG_STORAGE_KEY)
  } catch {
    // Private mode. memorySentFlags is already cleared.
  }
}
